import { UseQueryResult } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ExpandDownArrow from 'components/DesignSystems/buttons/ExpandDownArrow';
import ExpandRightArrow from 'components/DesignSystems/buttons/ExpandRightArrow';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import Loading from 'components/Loading/Loading';
import { GroupRosterItem } from 'hooks/incident/useRoster';
import React from 'react';
import { primaryValue } from 'utils/contactMethods';

type AssignedIncident = {
  incident_id: string;
  incident_name: string;
  position_name: string;
};

const VISIBLE_INCIDENT_COUNT = 2;

function AssignedIncidentsCell({
  incidents,
}: {
  incidents: AssignedIncident[] | undefined;
}) {
  const names = (incidents ?? []).map((incident) => incident.incident_name);
  if (names.length === 0) return null;
  if (names.length <= VISIBLE_INCIDENT_COUNT) {
    return <>{names.join(', ')}</>;
  }

  const visible = names.slice(0, VISIBLE_INCIDENT_COUNT);
  const overflow = names.slice(VISIBLE_INCIDENT_COUNT);
  return (
    <>
      {visible.join(', ')},{' '}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="tw:cursor-default tw:underline tw:decoration-dotted tw:underline-offset-2">
                {overflow.length} more
              </span>
            }
          />
          <TooltipContent side="top">
            {overflow.map((name) => (
              <div key={name}>{name}</div>
            ))}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </>
  );
}

type MemberSelectTableProps = {
  groupRoster: UseQueryResult<GroupRosterItem[], Error>;
  currentPositionMembers: RosterEntry[];
  onSelectMembers: (selectedMembers: GroupRosterItem[]) => void;
  closeTable: () => void;
  selectingLeader?: boolean;
};

const MemberSelectTable: React.FC<MemberSelectTableProps> = ({
  groupRoster,
  currentPositionMembers,
  onSelectMembers,
  closeTable,
  selectingLeader = true,
}) => {
  const [selectedMembers, setSelectedMembers] =
    React.useState<RowSelectionState>({});
  const [filterText, setFilterText] = React.useState('');
  const {
    data: groupRosterData,
    isLoading: isLoadingGroupRoster,
    isError: isErrorGroupRoster,
  } = groupRoster;

  const validRosterMembers = React.useMemo(() => {
    if (!groupRosterData) return [];
    const currentMemberIds = new Set(
      currentPositionMembers.map((member) => member.userId)
    );
    return groupRosterData.filter(
      (member) => !currentMemberIds.has(member.user_guid)
    );
  }, [groupRosterData, currentPositionMembers]);

  const filteredData = React.useMemo(
    () =>
      validRosterMembers.filter((member) => {
        if (member.user_name) {
          return (
            member.user_name.toLowerCase().includes(filterText.toLowerCase()) ||
            member.email_address
              .toLowerCase()
              .includes(filterText.toLowerCase())
          );
        } else {
          return member.email_address
            .toLowerCase()
            .includes(filterText.toLowerCase());
        }
      }),
    [validRosterMembers, filterText]
  );

  const assignMembers = (members: GroupRosterItem[]) => {
    onSelectMembers(members);
    closeTable();
  };

  return (
    <div className="tw:mb-4 tw:rounded-lg tw:border tw:border-border tw:px-3 tw:py-1">
      {isErrorGroupRoster && (
        <div className="d-flex align-items-center justify-content-center text-danger">
          Error loading group roster: {groupRoster.error.message}
        </div>
      )}
      {isLoadingGroupRoster && (
        <div className="d-flex align-items-center justify-content-center">
          <Loading />
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-row">
        <div className="text-secondary ms-3">
          {Object.keys(selectedMembers).length || 0} selected
        </div>
        <div className="tw:w-full tw:max-w-[38vw]">
          <Input
            value={filterText}
            onChange={(e) => {
              setFilterText(e.target.value);
              setSelectedMembers({});
            }}
            placeholder="Search members..."
          />
        </div>
      </div>
      <div className="tw:mb-2 tw:flex tw:gap-2">
        <Button
          type="button"
          onClick={() => {
            assignMembers(
              validRosterMembers.filter(
                (member) => selectedMembers[member.user_guid]
              )
            );
          }}
          disabled={Object.keys(selectedMembers).length === 0}
        >
          {selectingLeader ? 'Set as Leader' : 'Send Activation Request'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setSelectedMembers({});
            closeTable();
          }}
        >
          Cancel
        </Button>
      </div>
      <PratusTable
        columns={[
          {
            id: 'expander',
            cell: ({ row }) => {
              const expandable = row.getCanExpand?.();
              if (!expandable) return null;
              return row.getIsExpanded() ? (
                <ExpandDownArrow onClick={() => row.toggleExpanded()} />
              ) : (
                <ExpandRightArrow onClick={() => row.toggleExpanded()} />
              );
            },
            meta: { customSize: true, headerAlign: 'center', align: 'center' },
            size: 42,
          },
          {
            id: 'selector',
            cell: ({ row }) => {
              const selectable = row.getCanSelect?.();
              if (!selectable) return null;
              return (
                <div
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    row.toggleSelected();
                  }}
                >
                  <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(checked) => {
                      row.toggleSelected(!!checked);
                    }}
                    aria-label={`Select ${row.original.user_name || row.original.email_address}`}
                  />
                </div>
              );
            },
            meta: { customSize: true, headerAlign: 'center', align: 'center' },
            size: 42,
          },
          {
            header: 'Name',
            accessorKey: 'user_name',
            cell: ({ cell }) => {
              return cell.getValue() || cell.row.original.email_address;
            },
          },
          {
            header: 'Assigned To Incidents',
            accessorKey: 'assigned_incidents',
            cell: ({ cell }) => (
              <AssignedIncidentsCell
                incidents={cell.getValue<AssignedIncident[]>()}
              />
            ),
          },
          {
            id: 'assign',
            header: '',
            cell: ({ row }) => (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => assignMembers([row.original])}
              >
                Assign
              </Button>
            ),
            meta: { customSize: true, headerAlign: 'center', align: 'center' },
            size: 96,
          },
        ]}
        data={filteredData}
        expansion={{
          getRowCanExpand(_row) {
            return true;
          },
          expandedRowModel: (row: GroupRosterItem) => {
            return (
              <div className="d-flex justify-content-left flex-row">
                <div className="d-flex flex-column me-3 mb-1">
                  <div className="text-muted mt-1 mb-1">EMAIL</div>
                  <span>{row.email_address}</span>
                </div>
                <div className="d-flex flex-column mb-1">
                  <div className="text-muted mt-1 mb-1">PHONE</div>
                  <span>
                    {primaryValue(row.contact_methods, 'sms') ||
                      primaryValue(row.contact_methods, 'voice') ||
                      ''}
                  </span>
                </div>
              </div>
            );
          },
        }}
        selection={{
          onRowSelectionChange: setSelectedMembers,
          selectedRows: selectedMembers,
          enableMultiRowSelection: !selectingLeader,
        }}
        getRowId={(row) => row.user_guid}
      />
    </div>
  );
};

export default MemberSelectTable;
